import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '@/shared/lib/i18n/use-language'
import { useUpdateUserPreferences } from '@/shared/hooks/use-update-user-preferences'
import { useUser } from '@/shared/hooks/use-user'
import { useTalkedLanguage } from '@/shared/hooks/use-talked-language'
import {
  LANGUAGE_TO_SERVER,
  SERVER_LANGUAGE_MAP,
} from '@/shared/constants/language'
import type { SelectOption } from '@/components/molecules/select-field'
import type {
  UserGender,
  UserContactChannel,
} from '@/shared/types/global.types'

export type { UserGender, UserContactChannel }

interface UseRentersStepTwoPageOptions {
  onContinue: () => void
  onBack: () => void
}

export function useRentersStepTwoPage({
  onContinue,
  onBack,
}: UseRentersStepTwoPageOptions) {
  const { t } = useTranslation()
  const { user, isLoading } = useUser()
  const { currentLanguage, setLanguage } = useLanguage()
  const { mutate: updatePreferences } = useUpdateUserPreferences()
  const { talkedLanguage, setTalkedLanguage } = useTalkedLanguage(
    user,
    currentLanguage,
  )

  const genderOptions = useMemo<SelectOption[]>(
    () => [
      {
        value: 'Woman',
        label: t('onboarding.renters.stepTwo.identityOptions.female'),
      },
      {
        value: 'Man',
        label: t('onboarding.renters.stepTwo.identityOptions.male'),
      },
      {
        value: 'NonBinary',
        label: t('onboarding.renters.stepTwo.identityOptions.nonBinary'),
      },
      {
        value: 'PreferNotToSay',
        label: t('onboarding.renters.stepTwo.identityOptions.preferNotToSay'),
      },
    ],
    [t],
  )

  const [gender, setGender] = useState<UserGender | ''>('')
  const [channels, setChannels] = useState<Set<UserContactChannel>>(new Set())

  useEffect(() => {
    if (!user) return

    if (user.gender) setGender(user.gender)
    if (user.contactChannels?.length) setChannels(new Set(user.contactChannels))
    if (user.language) {
      const lang = SERVER_LANGUAGE_MAP[user.language]
      if (lang) setLanguage(lang)
    }
  }, [user])

  const toggleChannel = useCallback((channel: UserContactChannel) => {
    setChannels((prev) => {
      const next = new Set(prev)
      if (next.has(channel)) next.delete(channel)
      else next.add(channel)
      return next
    })
  }, [])

  const handleContinue = useCallback(() => {
    const contactChannels = Array.from(channels)
    const preferences = {
      talkedLanguage: LANGUAGE_TO_SERVER[talkedLanguage],
      ...(gender && { gender }),
      ...(contactChannels.length > 0 && { contactChannels }),
    }

    updatePreferences(preferences)

    onContinue()
  }, [talkedLanguage, gender, channels, updatePreferences, onContinue])

  const isFormValid = gender !== '' && channels.size > 0

  return {
    talkedLanguage,
    setTalkedLanguage,
    gender,
    setGender,
    genderOptions,
    channels,
    toggleChannel,
    handleContinue,
    handleBack: onBack,
    isLoading,
    isFormValid,
  }
}
